import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { PiXLight, PiCloudArrowUpLight } from 'react-icons/pi';
import { MdCheckCircle } from 'react-icons/md';
import Button from '../shared/TailwindComponents/Button';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import apis from '../../utils/apis';
import Spinner from '../shared/TailwindComponents/Spinner';

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName?: string;
  onUploadComplete?: () => void;
}

interface DocumentUploadModalContentProps {
  clientId: string;
  clientName?: string;
  onClose: () => void;
  onUploadComplete?: () => void;
}

interface UploadedDocument {
  uuid: string;
  name: string;
  size: number;
  status: 'success' | 'error';
  error?: string;
}

interface UploadResult {
  ok: boolean;
  filesUploaded: UploadedDocument[];
  filesRejected: UploadedDocument[];
  message?: string;
}

/**
 * DocumentUploadModalContent - Pure content component without Dialog wrapper
 * Used within GlobalModal to avoid nested dialog issues
 */
export const DocumentUploadModalContent: React.FC<DocumentUploadModalContentProps> = ({
  clientId,
  clientName = 'Contact',
  onClose,
  onUploadComplete,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);

  const validationSchema = Yup.object().shape({
    files: Yup.array().min(1, t('Please select at least one file')),
  });

  const formik = useFormik({
    initialValues: {
      files: [] as File[],
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleUploadFiles(values.files);
    },
  });

  const handleUploadFiles = async (files: File[]) => {
    if (files.length === 0) return;

    try {
      setIsSubmitting(true);
      const filesUploaded: UploadedDocument[] = [];
      const filesRejected: UploadedDocument[] = [];

      for (const file of files) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('category', 'general');

          const resp = await apis({
            method: 'POST',
            url: `/clients/${clientId}/documents`,
            data: formData,
          });

          if ((resp.status === 200 || resp.status === 201) && resp.data?.success) {
            filesUploaded.push({
              uuid: resp.data.document_uuid || resp.data.document?.uuid || file.name,
              name: file.name,
              size: file.size,
              status: 'success',
            });
          } else {
            filesRejected.push({
              uuid: `rejected-${file.name}`,
              name: file.name,
              size: file.size,
              status: 'error',
              error: resp.data?.message || t('Upload failed'),
            });
          }
        } catch (err: any) {
          filesRejected.push({
            uuid: `error-${file.name}`,
            name: file.name,
            size: file.size,
            status: 'error',
            error: err.message || t('Network error'),
          });
        }
      }

      const uploadResult: UploadResult = {
        ok: filesRejected.length === 0,
        filesUploaded,
        filesRejected,
        message:
          filesRejected.length === 0
            ? t('All documents uploaded successfully')
            : filesUploaded.length > 0
            ? t('Some documents uploaded')
            : t('Upload failed'),
      };

      setResult(uploadResult);

      if (uploadResult.ok && onUploadComplete) {
        onUploadComplete();
      }
    } catch (err) {
      setResult({
        ok: false,
        filesUploaded: [],
        filesRejected: files.map((f) => ({
          uuid: `error-${f.name}`,
          name: f.name,
          size: f.size,
          status: 'error',
          error: 'Upload failed',
        })),
        message: t('Upload failed'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      formik.resetForm();
      setResult(null);
      onClose();
    }
  };

  // Show results screen
  if (result && !isSubmitting) {
    return (
      <div className="space-y-6 max-h-96 overflow-y-auto custom-scrollbar-main px-4 pb-4">
        {result.filesUploaded.length > 0 && (
          <div>
            <div className="font-medium mb-3 text-gray-900 dark:text-gray-100">
              {t('Uploaded')} ({result.filesUploaded.length})
            </div>
            <div className="space-y-2">
              {result.filesUploaded.map((f) => (
                <div
                  key={f.uuid}
                  className="flex items-center gap-3 p-3 rounded-lg bg-success-50 dark:bg-success-950/20 border border-success-200 dark:border-success-800"
                >
                  <MdCheckCircle className="flex-shrink-0 h-5 w-5 text-success-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                      {f.name}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {(f.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {result.filesRejected.length > 0 && (
          <div>
            <div className="font-medium mb-3 text-gray-900 dark:text-gray-100">
              {t('Failed')} ({result.filesRejected.length})
            </div>
            <div className="space-y-2">
              {result.filesRejected.map((f, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-3 rounded-lg bg-danger-50 dark:bg-danger-950/20 border border-danger-200 dark:border-danger-800"
                >
                  <div className="flex-shrink-0 h-5 w-5 text-danger-600 mt-0.5">
                    ✕
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                      {f.name}
                    </p>
                    <p className="text-xs text-danger-600 dark:text-danger-400">
                      {f.error}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
          <Button variant="primary" size="small" onClick={handleClose}>
            {t('Done')}
          </Button>
        </div>
      </div>
    );
  }

  // Show uploading progress
  if (isSubmitting) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Spinner size="sm" className="!h-12 !w-12 mb-4" />
        <p className="text-gray-600 dark:text-gray-400">
          {t('Uploading documents...')}
        </p>
      </div>
    );
  }

  // Show upload form
  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4 px-4 pb-4">
      <div className="max-h-96 overflow-y-auto">
        <FileUploader
          files={formik.values.files}
          onFilesChange={(files) => formik.setFieldValue('files', files)}
        />
      </div>

      {formik.touched.files && formik.errors.files && (
        <p className="text-sm text-danger-600 dark:text-danger-400">
          {typeof formik.errors.files === 'string'
            ? formik.errors.files
            : t('Please select at least one file')}
        </p>
      )}

      <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <Button
          type="button"
          variant="neutral"
          size="small"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          {t('Cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="small"
          disabled={formik.values.files.length === 0 || isSubmitting}
        >
          {isSubmitting ? t('Uploading...') : t('Upload')} ({formik.values.files.length})
        </Button>
      </div>
    </form>
  );
};

/**
 * DocumentUploadModal - Full standalone modal component with Dialog wrapper
 * Use when modal needs to be opened standalone (not within GlobalModal)
 */
const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  isOpen,
  onClose,
  clientId,
  clientName = 'Contact',
  onUploadComplete,
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-ink/20 backdrop-blur-[2px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <PiCloudArrowUpLight className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {t('For')} <span className="font-medium">{clientName}</span>
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  >
                    <PiXLight className="h-5 w-5" />
                  </button>
                </div>

                {/* Content */}
                <DocumentUploadModalContent
                  clientId={clientId}
                  clientName={clientName}
                  onClose={onClose}
                  onUploadComplete={onUploadComplete}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DocumentUploadModal;
